import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/modal';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import LoginModal from '../auth/LoginModal';

interface ShareLoginPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onShareWithoutLogin: () => void;
  title?: string;
}

export const ShareLoginPromptModal: React.FC<ShareLoginPromptModalProps> = ({
  isOpen,
  onClose,
  onShareWithoutLogin,
  title
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);

  if (!isOpen) return null;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={title || t('share.loginPrompt.title', 'Share Your Spinner')}>
        <div className="space-y-6">
          <div className="text-gray-700 dark:text-gray-300">
            <h3 className="font-semibold mb-3">{t('share.loginPrompt.benefits', 'Benefits of logging in:')}</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>{t('share.loginPrompt.benefit1', 'Save your shared links for up to 365 days')}</li>
              <li>{t('share.loginPrompt.benefit2', 'Manage and reshare your saved spinners')}</li>
              <li>{t('share.loginPrompt.benefit3', 'Access your spinner history')}</li>
            </ul>
            {/* Render note as bulleted list */}
            {(() => {
              const noteLines = t('share.loginPrompt.note', 'Note: Shared links without login will expire in 7 days. Save without login will save in local cache.').split('\n');
              return (
                <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-semibold">{noteLines[0]}</span>
                  <ul className="list-disc ml-5 mt-1">
                    {noteLines.slice(1).map((line, idx) => (
                      <li key={idx}>{line.replace(/^•\s*/, '')}</li>
                    ))}
                  </ul>
                </div>
              );
            })()}
          </div>

          <div className="flex flex-col gap-3">
            <Button 
              onClick={() => setShowLoginModal(true)}
              className="w-full"
            >
              {t('share.loginPrompt.loginButton', 'Login to Share')}
            </Button>
            <Button 
              onClick={onShareWithoutLogin}
              variant="outline"
              className="w-full"
            >
              {t('share.loginPrompt.shareWithoutLogin', 'Share Without Login')}
            </Button>
          </div>
        </div>
      </Modal>

      <LoginModal 
        isOpen={showLoginModal} 
        onRequestClose={() => setShowLoginModal(false)} 
      />
    </>
  );
}; 